import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import { syncCapacitorStatusBar } from '../utils/capacitorNative';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'erp_theme';

function readStoredTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (_) { /* ignore */ }
  return 'dark';
}

const ThemeContext = createContext({
  theme: 'dark',
  setTheme: () => {},
  toggleTheme: () => {},
});

export function ThemeProvider({ children }) {
  const { user, isAuthenticated, patchUser } = useAuth();
  const [theme, setThemeState] = useState(readStoredTheme);

  // Logged-in: server preference (NULL → dark)
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    setThemeState(user.theme === 'light' ? 'light' : 'dark');
  }, [isAuthenticated, user?.id, user?.theme]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) { /* ignore */ }
    syncCapacitorStatusBar(theme);
  }, [theme]);

  const persistTheme = useCallback(
    async (next) => {
      const v = next === 'light' ? 'light' : 'dark';
      const prev = theme;
      setThemeState(v);
      if (!isAuthenticated) {
        return;
      }
      try {
        const res = await api.patch('/auth/me', { theme: v });
        const data = res.data?.data;
        if (data && typeof data === 'object') {
          patchUser(data);
        } else {
          patchUser({ theme: v });
        }
      } catch (_) {
        setThemeState(prev);
        throw _;
      }
    },
    [theme, isAuthenticated, patchUser],
  );

  const setTheme = useCallback(
    async (next) => {
      await persistTheme(next);
    },
    [persistTheme],
  );

  const toggleTheme = useCallback(async () => {
    await persistTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, persistTheme]);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
